import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ArrowLeft, Plus, Trash2, BookOpen, Loader2, Star, Search } from 'lucide-react';
import { LIBRARY } from '@/constants/testIds';
import { toast } from 'sonner';
import axios from 'axios';

const Library = () => {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [newBook, setNewBook] = useState({
    title: '', author: '', genre: '', year: '', rating: '', cover_url: null
  });

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/books`,
        { withCredentials: true }
      );
      setBooks(data);
    } catch (error) {
      toast.error('Failed to load books');
    } finally {
      setLoading(false);
    }
  };

  const searchOpenLibrary = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/books/search`,
        { params: { q: searchQuery }, withCredentials: true }
      );
      setSearchResults(data.results || []);
    } catch (error) {
      toast.error('Failed to search books');
    } finally {
      setSearching(false);
    }
  };

  const addBookFromSearch = async (bookData) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/books`,
        {
          title: bookData.title,
          author: bookData.author,
          genre: bookData.genre,
          year: bookData.year || null,
          cover_url: bookData.cover_url
        },
        { withCredentials: true }
      );
      toast.success(`"${bookData.title}" added to your library!`);
      fetchBooks();
    } catch (error) {
      toast.error('Failed to add book');
    }
  };

  const addBookManual = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/books`,
        {
          title: newBook.title,
          author: newBook.author,
          genre: newBook.genre,
          year: newBook.year ? parseInt(newBook.year) : null,
          rating: newBook.rating ? parseInt(newBook.rating) : null
        },
        { withCredentials: true }
      );
      toast.success('Book added successfully!');
      setNewBook({ title: '', author: '', genre: '', year: '', rating: '', cover_url: null });
      setDialogOpen(false);
      fetchBooks();
    } catch (error) {
      toast.error('Failed to add book');
    }
  };

  const deleteBook = async (bookId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/books/${bookId}`,
        { withCredentials: true }
      );
      toast.success('Book removed');
      fetchBooks();
    } catch (error) {
      toast.error('Failed to delete book');
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setManualMode(false);
    setNewBook({ title: '', author: '', genre: '', year: '', rating: '', cover_url: null });
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] paper-texture">
      <div className="container mx-auto px-6 py-12">
        <Button
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6 text-[#5C5955] hover:text-[#6B7A4F]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div data-testid={LIBRARY.container} className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl sm:text-4xl font-light text-[#2D2A26]">
                My <span className="font-bold">Library</span>
              </h1>
              <p className="text-[#5C5955] mt-2">{books.length} books in your collection</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => open ? setDialogOpen(true) : closeDialog()}>
              <DialogTrigger asChild>
                <Button
                  data-testid={LIBRARY.addBookBtn}
                  className="bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Book
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add a Book to Your Library</DialogTitle>
                  <DialogDescription className="sr-only">Manage details here</DialogDescription>
                </DialogHeader>

                {!manualMode ? (
                  <>
                    <form onSubmit={searchOpenLibrary} className="flex gap-2">
                      <Input
                        placeholder="Search by title or author..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1"
                        data-testid="library-search-input"
                      />
                      <Button
                        type="submit"
                        disabled={searching || !searchQuery.trim()}
                        className="bg-[#6B7A4F] hover:bg-[#57653F] text-white"
                        data-testid="library-search-btn"
                      >
                        {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      </Button>
                    </form>

                    <div className="max-h-[400px] overflow-y-auto space-y-2 mt-2">
                      {searchResults.length === 0 && !searching && searchQuery && (
                        <p className="text-center text-[#5C5955] py-8">No results. Try another search.</p>
                      )}
                      {searchResults.map((result, idx) => (
                        <div
                          key={idx}
                          className="flex gap-3 p-3 border border-[#E1D9CD] rounded-xl hover:bg-[#F4F1EA]/50"
                        >
                          {result.cover_url ? (
                            <img
                              src={result.cover_url}
                              alt={result.title}
                              className="w-14 h-20 object-cover rounded shadow-sm"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-14 h-20 bg-[#E2E6D5] rounded flex items-center justify-center">
                              <BookOpen className="w-6 h-6 text-[#6B7A4F]" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-[#2D2A26] truncate">{result.title}</h4>
                            <p className="text-sm text-[#5C5955] truncate">by {result.author}</p>
                            <p className="text-xs text-[#5C5955]">
                              {result.year && `${result.year} · `}{result.genre}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => addBookFromSearch(result)}
                            className="bg-[#8E9E82] hover:bg-[#7A8A72] text-white rounded-full self-center"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>

                    <div className="text-center pt-2 border-t border-[#E1D9CD]">
                      <button
                        type="button"
                        onClick={() => setManualMode(true)}
                        className="text-sm text-[#6B7A4F] hover:underline"
                      >
                        Can't find it? Add manually
                      </button>
                    </div>
                  </>
                ) : (
                  <form onSubmit={addBookManual} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title *</Label>
                      <Input
                        id="title"
                        value={newBook.title}
                        onChange={(e) => setNewBook({ ...newBook, title: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="author">Author *</Label>
                      <Input
                        id="author"
                        value={newBook.author}
                        onChange={(e) => setNewBook({ ...newBook, author: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="genre">Genre *</Label>
                      <Input
                        id="genre"
                        value={newBook.genre}
                        onChange={(e) => setNewBook({ ...newBook, genre: e.target.value })}
                        required
                        placeholder="e.g., Fiction, Mystery"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="year">Year</Label>
                        <Input
                          id="year"
                          type="number"
                          value={newBook.year}
                          onChange={(e) => setNewBook({ ...newBook, year: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="rating">Rating (1-5)</Label>
                        <Input
                          id="rating"
                          type="number"
                          min="1"
                          max="5"
                          value={newBook.rating}
                          onChange={(e) => setNewBook({ ...newBook, rating: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setManualMode(false)}
                        className="flex-1"
                      >
                        ← Back to Search
                      </Button>
                      <Button type="submit" className="flex-1 bg-[#6B7A4F] hover:bg-[#57653F] text-white">
                        Add to Library
                      </Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-[#6B7A4F] mx-auto" />
            </div>
          ) : books.length === 0 ? (
            <div className="text-center py-12 glass-card rounded-2xl">
              <BookOpen className="w-16 h-16 text-[#6B7A4F] mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold text-[#2D2A26] mb-2">No books yet</h3>
              <p className="text-[#5C5955]">Start building your library by adding books you've read</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {books.map((book) => (
                <div
                  key={book.id}
                  data-testid={LIBRARY.bookCard}
                  className="book-card group"
                >
                  <div className="relative mb-3 rounded-lg overflow-hidden bg-[#F4F1EA] aspect-[2/3]">
                    {book.cover_url ? (
                      <img
                        src={book.cover_url}
                        alt={book.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#E2E6D5] to-[#E1D9CD] ${book.cover_url ? 'hidden' : 'flex'}`}
                      style={{ display: book.cover_url ? 'none' : 'flex' }}
                    >
                      <BookOpen className="w-12 h-12 text-[#6B7A4F] opacity-60" strokeWidth={1.5} />
                    </div>
                    <button
                      data-testid={LIBRARY.deleteBookBtn}
                      onClick={() => deleteBook(book.id)}
                      className="absolute top-2 right-2 bg-white/90 hover:bg-red-500 hover:text-white text-red-500 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <h3 data-testid={LIBRARY.bookTitle} className="font-bold text-[#2D2A26] mb-1 line-clamp-2">
                    {book.title}
                  </h3>
                  <p data-testid={LIBRARY.bookAuthor} className="text-sm text-[#5C5955] mb-2 line-clamp-1">
                    by {book.author}
                  </p>
                  <div className="flex items-center flex-wrap gap-2">
                    <span className="genre-badge">{book.genre}</span>
                    {book.year && (
                      <span className="text-xs text-[#5C5955]">({book.year})</span>
                    )}
                  </div>
                  {book.rating && (
                    <div className="flex items-center gap-0.5 mt-2">
                      {[...Array(book.rating)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-[#6B7A4F] text-[#6B7A4F]" />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Library;
